import {
  useState, useEffect, useContext, useRef, CSSProperties, FormEvent,
} from 'react';
import { useNavigate, useLocation } from 'react-router';
import { useSelector, useDispatch } from 'react-redux';
import { useTranslation, Trans } from 'react-i18next';
import type { TFunction } from 'i18next';
import Box from '@mui/material/Box';
import Pagination from '@mui/material/Pagination';
import ClickAwayListener from '@mui/material/ClickAwayListener';
import CircularProgress from '@mui/material/CircularProgress';
import SearchIcon from '@mui/icons-material/Search';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import SortIcon from '@mui/icons-material/Sort';
import PlayCircleIcon from '@mui/icons-material/PlayCircle';
import CloseIcon from '@mui/icons-material/Close';

import { FeedbackUIContext } from '#context/feedback-ui-context';
import {
  useGetProductsQuery,
  useGetProductCategoriesQuery,
} from '#services/product';
import {
  useGetCartMutation,
  useClearCartMutation,
} from '#services/cart';
import {
  formatCurrency,
} from '#common/utils';
import { CART_ITEM_TYPES } from '#common/constants';
import { enableMobileViewport, removeViewportMeta } from '#utils/viewport';

import {
  setPageIndex,
  setOrder,
  setOrderBy,
  setFilter,
  setFilterKey,
  setUserId,
  setSelectedCollections,
  setSelectedTags,
  toggleSelectedCollections,
  toggleSelectedTags,
} from './list.slice';
import './List.css';

interface ProductCollection {
  id?: string | number;
  name?: string;
  [key: string]: unknown;
}

interface ProductVideo {
  url?: string;
  [key: string]: unknown;
}

interface ProductItem {
  id: string | number;
  name?: string;
  price?: number;
  media?: string[];
  videos?: ProductVideo[];
  tags?: string[];
  collections?: ProductCollection[];
  [key: string]: unknown;
}

interface MediaContent {
  type: 'image' | 'video';
  src?: string;
  videoId?: string;
  thumbnailUrl?: string;
  embedUrl?: string;
}

interface ProductBadge {
  labelKey?: string;
  variant: string;
  label?: string;
}

interface CategoryCollection {
  id: string | number;
  name: string;
  [key: string]: unknown;
}

interface CartItemLike {
  type?: number;
  [key: string]: unknown;
}

const IMAGE_PLACEHOLDER = "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='680.764' height='528.354' viewBox='0 0 180.119 139.794'%3E%3Cg transform='translate(-13.59 -66.639)' paint-order='fill markers stroke'%3E%3Cpath fill='%23ebe3d4' d='M13.591 66.639H193.71v139.794H13.591z'/%3E%3Cpath d='m118.507 133.514-34.249 34.249-15.968-15.968-41.938 41.937H178.726z' opacity='.675' fill='%23fff'/%3E%3Ccircle cx='58.217' cy='108.555' r='11.773' opacity='.675' fill='%23fff'/%3E%3Cpath fill='none' d='M26.111 77.634h152.614v116.099H26.111z'/%3E%3C/g%3E%3C/svg%3E";

const SORT_OPTIONS = [{
  value: 'featured|desc',
  labelKey: 'sort.featured',
}, {
  value: 'name|asc',
  labelKey: 'sort.nameAsc',
}, {
  value: 'name|desc',
  labelKey: 'sort.nameDesc',
}, {
  value: 'price|asc',
  labelKey: 'sort.priceAsc',
}, {
  value: 'price|desc',
  labelKey: 'sort.priceDesc',
}];

function getMediaContent(item: ProductItem, isHovered: boolean): MediaContent {
  if (!item.media || !item.media.length) {
    return {
      type: 'image',
      src: IMAGE_PLACEHOLDER,
    };
  }

  if (!isHovered) {
    return {
      type: 'image',
      src: `/images/product_${item.id.toString().padStart(8, '0')}/${item.media[0]}`,
    };
  }

  const videoItem = item.videos && item.videos.find((v) => v.url);
  if (videoItem && videoItem.url) {
    const match = videoItem.url.match(/\/\/youtu\.be(\/)([a-zA-Z0-9-_]*)/)
      || videoItem.url.match(/(?:watch|shorts|live)(\?v=|\/)?([a-zA-Z0-9-_]*)/);

    if (match) {
      const videoId = match[2]!;
      return {
        type: 'video',
        videoId,
        thumbnailUrl: `https://img.youtube.com/vi/${videoId}/hqdefault.jpg`,
        embedUrl: `https://www.youtube.com/embed/${videoId}?autoplay=1&mute=1`,
      };
    }
  }

  if (item.media.length > 1) {
    return {
      type: 'image',
      src: `/images/product_${item.id.toString().padStart(8, '0')}/${item.media[1]}`,
    };
  }

  return {
    type: 'image',
    src: `/images/product_${item.id.toString().padStart(8, '0')}/${item.media[0]}`,
  };
}

function getProductBadge(item?: ProductItem | null): ProductBadge | null {
  if (!item) {
    return null;
  }

  const tags = Array.isArray(item.tags) ? item.tags.filter(Boolean) : [];
  const collections = Array.isArray(item.collections) ? item.collections : [];

  const newArrival = tags.find((tag) => /new/i.test(tag));
  if (newArrival) {
    return {
      labelKey: 'badge.new',
      variant: 'new',
    };
  }

  if (tags.length) {
    return {
      label: tags[0]!,
      variant: 'default',
    };
  }

  if (collections.length && collections[0]?.name) {
    return {
      label: collections[0].name,
      variant: 'default',
    };
  }

  return null;
}

function getSortLabelKey(sortValue: string): string {
  return SORT_OPTIONS.find((option) => option.value === sortValue)?.labelKey || 'sort.featured';
}

function getShopKicker(selectedCollectionItems: CategoryCollection[], t: TFunction): string {
  const prefix = t('kicker.shop');

  if (!selectedCollectionItems.length) {
    return prefix;
  }

  if (selectedCollectionItems.length === 1) {
    return `${prefix} • ${selectedCollectionItems[0]!.name}`;
  }

  if (selectedCollectionItems.length === 2) {
    return `${prefix} • ${selectedCollectionItems.map((item) => item.name).join(' • ')}`;
  }

  return `${prefix} • ${t('kicker.collectionsCount', { count: selectedCollectionItems.length })}`;
}

interface ProductCardProps {
  item: ProductItem;
  locationSearch: string;
}

function ProductCard(props: ProductCardProps) {
  const {
    item,
    locationSearch,
  } = props;
  const navigate = useNavigate();
  const { t } = useTranslation('shop');
  const [isHovered, setIsHovered] = useState(false);
  const mediaContent = getMediaContent(item, isHovered);
  const badge = getProductBadge(item);
  const imageSrc = mediaContent.type === 'video' ? mediaContent.thumbnailUrl : mediaContent.src;
  const imgRef = useRef<HTMLImageElement | null>(null);
  const [mediaLoaded, setMediaLoaded] = useState(false);

  useEffect(() => {
    const img = imgRef.current;
    setMediaLoaded(Boolean(img && img.complete && img.naturalWidth > 0));
  }, [imageSrc]);

  return (
    <button
      type="button"
      className="shop-card"
      onClick={() => { void navigate(`/shop/add?productId=${item.id}${locationSearch.replace('?', '&')}`); }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      onFocus={() => setIsHovered(true)}
      onBlur={() => setIsHovered(false)}
    >
      <div className={`shop-card-media ${mediaLoaded ? 'is-loaded' : ''}`}>
        {mediaContent.type === 'video' && isHovered ? (
          <div className="shop-card-video-frame">
            <iframe
              src={mediaContent.embedUrl}
              title={t('media.cardVideoPreview', { name: item.name })}
              frameBorder="0"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
            />
            <div className="shop-card-video-overlay" />
          </div>
        ) : (
          <img
            ref={imgRef}
            src={imageSrc}
            alt={item.name}
            loading="lazy"
            decoding="async"
            onLoad={() => setMediaLoaded(true)}
            onError={() => setMediaLoaded(true)}
          />
        )}

        {badge && (
          <div className="shop-card-badges">
            <span className={`shop-card-badge shop-card-badge--${badge.variant}`}>
              {badge.labelKey ? t(badge.labelKey) : badge.label}
            </span>
          </div>
        )}

        {item.videos && item.videos.some((v) => v.url) && (
          <span className="shop-card-video-indicator" aria-hidden="true">
            <PlayCircleIcon fontSize="inherit" />
          </span>
        )}
      </div>

      <div className="shop-card-meta">
        <div className="MuiImageListItemBar-title shop-card-title-block">
          <div className="shop-card-name">{item.name}</div>
          <div className="shop-card-price">{formatCurrency(item.price as number)}</div>
        </div>
        {Array.isArray(item.collections) && item.collections[0]?.name && (
          <div className="shop-card-code">
            {item.collections[0].name}
          </div>
        )}
      </div>
    </button>
  );
}

function ProductSkeletonCard() {
  return (
    <article className="shop-card shop-card--loading" aria-hidden="true">
      <div className="shop-card-media shop-card-media--loading" />
      <div className="shop-card-meta">
        <div className="shop-skeleton shop-skeleton--title" />
        <div className="shop-skeleton shop-skeleton--code" />
        <div className="shop-skeleton shop-skeleton--price" />
      </div>
    </article>
  );
}

interface ProductsProps {
  userId: string | number;
  filterForQuery: string;
}

function Products(props: ProductsProps) {
  const {
    userId,
    filterForQuery,
  } = props;

  const dispatch = useDispatch();
  const location = useLocation();
  const { t } = useTranslation('shop');
  const appConfig = useSelector((state: { appConfig: { shopAllColumns?: number } }) => state.appConfig);
  const filterKey = useSelector((state: { shopList: { filterKey: string } }) => state.shopList.filterKey);
  const order = useSelector((state: { shopList: { order: string } }) => state.shopList.order);
  const orderBy = useSelector((state: { shopList: { orderBy: string } }) => state.shopList.orderBy);
  const pageIndex = useSelector((state: { shopList: { pageIndex: number } }) => state.shopList.pageIndex);
  const pageSize = useSelector((state: { shopList: { pageSize: number } }) => state.shopList.pageSize);
  const rows = useSelector((state: { shopList: { rows: ProductItem[] } }) => state.shopList.rows);
  const rowCount = useSelector((state: { shopList: { rowCount: number } }) => state.shopList.rowCount);
  const selectedCollections = useSelector(
    (state: { shopList: { selectedCollections: Array<string | number> } }) => state.shopList.selectedCollections,
  );
  const selectedTags = useSelector(
    (state: { shopList: { selectedTags: string[] } }) => state.shopList.selectedTags,
  );

  const {
    simpleSnackbar,
  } = useContext(FeedbackUIContext)!;

  const confGrid = appConfig.shopAllColumns || 4;
  const effectivePageSize = confGrid % 2 === 0 ? pageSize : pageSize + 1;
  const pageCount = Math.max(1, Math.ceil(rowCount / effectivePageSize));
  const fromRow = rowCount === 0 ? 0 : pageIndex * effectivePageSize + 1;
  const toRow = Math.min((pageIndex + 1) * effectivePageSize, rowCount);
  const query = useGetProductsQuery({
    order,
    orderBy,
    filter: filterForQuery,
    filterKey,
    pageIndex,
    pageSize: effectivePageSize,
    collections: selectedCollections,
    tags: selectedTags,
    userId,
  }, {
    refetchOnMountOrArgChange: true,
  });

  useEffect(() => {
    if (query.error) {
      simpleSnackbar(query as unknown as Parameters<typeof simpleSnackbar>[0]);
    }
  }, [query.error]);

  const skeletonCount = Math.max(confGrid * 2, 4);
  const showUpdatingOverlay = query.isFetching && !!rows.length;

  return (
    <>
      <div className={`shop-grid-shell ${showUpdatingOverlay ? 'is-updating' : ''}`}>
        <div className="shop-grid" style={{ '--shop-columns': confGrid } as CSSProperties}>
          {query.isLoading && !rows.length && Array.from({ length: skeletonCount }).map((_, idx) => (
            <ProductSkeletonCard key={idx} />
          ))}

          {!query.isLoading && rows.map((item) => (
            <ProductCard
              key={item.id}
              item={item}
              locationSearch={location.search}
            />
          ))}
        </div>

        {showUpdatingOverlay && (
          <div className="shop-grid-overlay" aria-live="polite">
            <CircularProgress size={18} thickness={4} />
            <span>{t('updatingStyles')}</span>
          </div>
        )}
      </div>
      {!!rows.length && (
        <Box className="shop-pagination">
          <Box component="span" className="shop-pagination-summary">
            <Trans
              ns="shop"
              i18nKey="pagination"
              values={{ from: fromRow, to: toRow, total: rowCount }}
              components={[<b key="from" />, <b key="to" />, <b key="total" />]}
            />
          </Box>
          <Box className="shop-pagination-spacer" />
          <Pagination
            page={pageIndex + 1}
            count={pageCount}
            onChange={(event, page) => {
              window.scrollTo({ top: 0, behavior: 'smooth' });
              dispatch(setPageIndex(page - 1));
            }}
            showFirstButton
            showLastButton
            siblingCount={1}
            boundaryCount={1}
          />
        </Box>
      )}
      {!query.isLoading && !rows.length && (
        <Box className="shop-empty-state">
          {t('emptyState')}
        </Box>
      )}
    </>
  );
}

export default function Shop() {
  const userInfo = useSelector(
    (state: { auth: { userInfo: { isCustomer?: boolean; id?: string | number } | null } }) => state.auth.userInfo,
  );
  const selectedCollections = useSelector(
    (state: { shopList: { selectedCollections: Array<string | number> } }) => state.shopList.selectedCollections,
  );
  const selectedTags = useSelector(
    (state: { shopList: { selectedTags: string[] } }) => state.shopList.selectedTags,
  );
  const filter = useSelector((state: { shopList: { filter: string } }) => state.shopList.filter);
  const order = useSelector((state: { shopList: { order: string } }) => state.shopList.order);
  const orderBy = useSelector((state: { shopList: { orderBy: string } }) => state.shopList.orderBy);
  const [filterForQuery, setFilterForQuery] = useState(filter);
  const [searchValue, setSearchValue] = useState(filter);
  const [sortControlValue, setSortControlValue] = useState(`${orderBy}|${order}`);
  const [openFilter, setOpenFilter] = useState<string | null>(null);
  const [collectionSearch, setCollectionSearch] = useState('');
  const [tagSearch, setTagSearch] = useState('');

  const dispatch = useDispatch();
  const location = useLocation();
  const navigate = useNavigate();
  const { t } = useTranslation('shop');

  const params = new URLSearchParams(location.search);
  const isInventory = (params.get('inventory') === 'true');

  const userId: string | number = Number(params.get('userId')) || (
    userInfo?.isCustomer
      ? (userInfo.id ?? '')
      : ''
  );

  useEffect(() => {
    enableMobileViewport();

    return () => {
      removeViewportMeta();
    };
  }, []);

  useEffect(() => {
    dispatch(setUserId(userId));
  }, [userId]);

  useEffect(() => {
    setSearchValue(filter);
    setFilterForQuery(filter);
  }, [filter]);

  useEffect(() => {
    setSortControlValue(`${orderBy}|${order}`);
  }, [order, orderBy]);

  const { data: categoriesData } = useGetProductCategoriesQuery({ userId }) as {
    data?: { collections?: CategoryCollection[]; tags?: string[] };
  };
  const { collections, tags } = categoriesData || {};
  const sortedCollections = collections
    ? [...collections].sort((a, b) => a.name.localeCompare(b.name))
    : [];
  const sortedTags = tags ? [...tags].sort((a, b) => a.localeCompare(b)) : [];
  const filteredCollections = sortedCollections.filter((item) => (
    item.name.toLowerCase().includes(collectionSearch.trim().toLowerCase())
  ));
  const filteredTags = sortedTags.filter((item) => (
    item.toLowerCase().includes(tagSearch.trim().toLowerCase())
  ));

  const [getCart] = useGetCartMutation();
  const [clearCart] = useClearCartMutation();

  const {
    showDialog,
    hideDialog,
    setDialogData,
  } = useContext(FeedbackUIContext)!;

  useEffect(() => {
    const navState = location.state as { fromPanel?: boolean } | null;
    if (!navState || !navState.fromPanel) {
      return;
    }

    void getCart(undefined).then((res) => {
      const result = res as { error?: unknown; data?: { items?: CartItemLike[] } };
      if (result.error) {
        return;
      }
      let shouldClear = false;
      if (result.data?.items) {
        shouldClear = result.data.items.some((itm) => (
          isInventory
            ? itm.type !== CART_ITEM_TYPES.ADD_INVENTORY
            : itm.type === CART_ITEM_TYPES.ADD_INVENTORY
        ));
      }

      if (!shouldClear) {
        return;
      }

      setDialogData({
        title: t('clearCart.title'),
        text: isInventory ? t('clearCart.textInventory') : t('clearCart.textCustomer'),
        disagree: t('clearCart.back'),
        agree: t('clearCart.confirm'),
        onDisagree: () => {
          hideDialog();
          void navigate(-1);
        },
        onAgree: () => {
          hideDialog();
          void clearCart({ isInventory });
        },
      });
      showDialog();
    });
  }, []);

  const selectedCollectionItems = sortedCollections.filter((item) => selectedCollections.includes(item.id));
  const activeFilterCount = selectedCollections.length + selectedTags.length;
  const sortLabel = t(getSortLabelKey(sortControlValue));
  const shopKicker = getShopKicker(selectedCollectionItems, t);

  const submitSearch = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const cleanFilter = searchValue.trim();
    dispatch(setFilterKey('name'));
    dispatch(setFilter(cleanFilter));
    dispatch(setPageIndex(0));
    setFilterForQuery(cleanFilter);
  };

  const clearAllFilters = () => {
    dispatch(setSelectedCollections([]));
    dispatch(setSelectedTags([]));
    dispatch(setPageIndex(0));
  };

  return (
    <ClickAwayListener onClickAway={() => setOpenFilter(null)}>
      <div>
        <div className="shop-page-shell">
          <div className="shop-page-header">
            <div className="shop-page-kicker">{shopKicker}</div>
          </div>

          <div className="shop-toolbar">
            <form className="shop-search" onSubmit={submitSearch}>
              <button type="submit" className="shop-search-icon" aria-label={t('search.aria')}>
                <SearchIcon fontSize="inherit" />
              </button>
              <input
                value={searchValue}
                placeholder={t('search.placeholder')}
                onChange={(event) => {
                  const newValue = event.target.value;
                  setSearchValue(newValue);

                  if (newValue === '' && filter !== '') {
                    dispatch(setFilter(''));
                    dispatch(setPageIndex(0));
                    setFilterForQuery('');
                  }
                }}
              />
            </form>

            <div className={`shop-filter ${openFilter === 'collections' ? 'is-open' : ''}`}>
              <button
                type="button"
                className={`shop-filter-button ${selectedCollections.length ? 'is-active' : ''}`}
                onClick={() => setOpenFilter(openFilter === 'collections' ? null : 'collections')}
                aria-expanded={openFilter === 'collections'}
              >
                <span>{t('filter.collection')}</span>
                {!!selectedCollections.length && (
                  <span className="shop-filter-count">{selectedCollections.length}</span>
                )}
                <ExpandMoreIcon fontSize="inherit" />
              </button>

              <div className="shop-filter-menu">
                <div className="shop-filter-search">
                  <input
                    value={collectionSearch}
                    placeholder={t('filter.searchCollections')}
                    onChange={(event) => setCollectionSearch(event.target.value)}
                  />
                </div>
                <div className="shop-filter-options">
                  {filteredCollections.map((item) => (
                    <label key={item.id} className="shop-filter-option">
                      <input
                        type="checkbox"
                        checked={selectedCollections.includes(item.id)}
                        onChange={() => dispatch(toggleSelectedCollections(item.id))}
                      />
                      <span className="shop-filter-option-label">{item.name}</span>
                    </label>
                  ))}
                  {!filteredCollections.length && (
                    <div className="shop-filter-empty">{t('filter.noCollections')}</div>
                  )}
                </div>
                <div className="shop-filter-footer">
                  <button
                    type="button"
                    onClick={() => {
                      dispatch(setSelectedCollections([]));
                      dispatch(setPageIndex(0));
                    }}
                  >
                    {t('actions.clear')}
                  </button>
                  <button type="button" onClick={() => setOpenFilter(null)}>{t('actions.done')}</button>
                </div>
              </div>
            </div>

            <div className={`shop-filter ${openFilter === 'tags' ? 'is-open' : ''}`}>
              <button
                type="button"
                className={`shop-filter-button ${selectedTags.length ? 'is-active' : ''}`}
                onClick={() => setOpenFilter(openFilter === 'tags' ? null : 'tags')}
                aria-expanded={openFilter === 'tags'}
              >
                <span>{t('filter.tags')}</span>
                {!!selectedTags.length && (
                  <span className="shop-filter-count">{selectedTags.length}</span>
                )}
                <ExpandMoreIcon fontSize="inherit" />
              </button>

              <div className="shop-filter-menu shop-filter-menu--wide">
                <div className="shop-filter-search">
                  <input
                    value={tagSearch}
                    placeholder={t('filter.searchTags')}
                    onChange={(event) => setTagSearch(event.target.value)}
                  />
                </div>
                <div className="shop-filter-options">
                  {filteredTags.map((item) => (
                    <label key={item} className="shop-filter-option">
                      <input
                        type="checkbox"
                        checked={selectedTags.includes(item)}
                        onChange={() => dispatch(toggleSelectedTags(item))}
                      />
                      <span className="shop-filter-option-label">{item}</span>
                    </label>
                  ))}
                  {!filteredTags.length && (
                    <div className="shop-filter-empty">{t('filter.noTags')}</div>
                  )}
                </div>
                <div className="shop-filter-footer">
                  <button
                    type="button"
                    onClick={() => {
                      dispatch(setSelectedTags([]));
                      dispatch(setPageIndex(0));
                    }}
                  >
                    {t('actions.clear')}
                  </button>
                  <button type="button" onClick={() => setOpenFilter(null)}>{t('actions.done')}</button>
                </div>
              </div>
            </div>

            {activeFilterCount > 0 && (
              <button type="button" className="shop-clear-button" onClick={clearAllFilters}>
                {t('actions.clearAll')}
              </button>
            )}

            <div className={`shop-filter shop-filter--sort ${openFilter === 'sort' ? 'is-open' : ''}`}>
              <button
                type="button"
                className="shop-filter-button"
                onClick={() => setOpenFilter(openFilter === 'sort' ? null : 'sort')}
                aria-expanded={openFilter === 'sort'}
              >
                <SortIcon fontSize="inherit" />
                <span>{t('sort.label', { value: sortLabel })}</span>
                <ExpandMoreIcon fontSize="inherit" />
              </button>

              <div className="shop-filter-menu shop-filter-menu--sort">
                <div className="shop-filter-options">
                  {SORT_OPTIONS.map((option) => (
                    <label key={option.value} className="shop-filter-option shop-filter-option--radio">
                      <input
                        type="radio"
                        name="shop-sort"
                        checked={sortControlValue === option.value}
                        onChange={() => {
                          const [key, direct] = option.value.split('|') as [string, string];
                          dispatch(setOrder(direct));
                          dispatch(setOrderBy(key));
                          setSortControlValue(option.value);
                          setOpenFilter(null);
                        }}
                      />
                      <span className="shop-filter-option-label">{t(option.labelKey)}</span>
                    </label>
                  ))}
                </div>
              </div>
            </div>
          </div>

          <div className="shop-active-filters">
            {selectedCollectionItems.map((item) => (
              <span key={item.id} className="shop-chip">
                <span className="shop-chip-label">{t('chip.collection')}</span>
                <span>{item.name}</span>
                <button type="button" onClick={() => dispatch(toggleSelectedCollections(item.id))} aria-label={t('chip.removeAria', { name: item.name })}>
                  <CloseIcon fontSize="inherit" />
                </button>
              </span>
            ))}

            {selectedTags.map((tag) => (
              <span key={tag} className={`shop-chip ${/new/i.test(tag) ? 'shop-chip--accent' : ''}`}>
                <span className="shop-chip-label">{t('chip.tag')}</span>
                <span>{tag}</span>
                <button type="button" onClick={() => dispatch(toggleSelectedTags(tag))} aria-label={t('chip.removeAria', { name: tag })}>
                  <CloseIcon fontSize="inherit" />
                </button>
              </span>
            ))}
          </div>

          <Products
            userId={userId}
            filterForQuery={filterForQuery}
          />
        </div>
      </div>
    </ClickAwayListener>
  );
}
